import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { siteInfo } from "@/mocks/siteInfo";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState<"success" | "error" | "">("");

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatusMsg(t("footer.subscribe_success"));
      setStatusType("success");
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch("http://xinxiust.com/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed.code === "OK") {
        setStatusMsg(t("footer.subscribe_success"));
        setStatusType("success");
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        if (serverMsg.toLowerCase().includes("spam")) {
          setStatusMsg(t("footer.subscribe_success"));
          setStatusType("success");
        } else {
          setStatusMsg(serverMsg || t("footer.subscribe_error"));
          setStatusType("error");
        }
      }
    } catch {
      setStatusMsg(t("footer.subscribe_error"));
      setStatusType("error");
    }
  };

  return (
    <footer className="px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-7xl mx-auto">
        <div className="bg-foreground-950 rounded-[24px] p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-6">
              <h3 className="text-2xl md:text-3xl font-light text-white tracking-tight">
                {t("footer.subscribe_title")}
              </h3>
              <p className="mt-3 text-sm text-gray-400 max-w-sm">
                {t("footer.subscribe_desc")}
              </p>
              <form onSubmit={handleSubscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
                <input
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.subscribe_placeholder")}
                  className="flex-1 bg-transparent border-0 border-b border-gray-600 px-1 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <input
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
                />
                <button
                  type="submit"
                  className="bg-white text-foreground-950 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
                >
                  {t("footer.subscribe_btn")}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </form>
              {statusMsg && (
                <p className={`mt-3 text-sm ${statusType === "success" ? "text-green-400" : "text-red-400"}`}>
                  {statusMsg}
                </p>
              )}
            </div>

            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold text-white uppercase tracking-wide">
                {t("footer.quick_links")}
              </h4>
              <ul className="mt-4 space-y-3">
                {[
                  { path: "/", label: t("nav.home") },
                  { path: "/category", label: t("nav.category") },
                  { path: "/article", label: t("nav.article") },
                  { path: "/about", label: t("nav.about") },
                ].map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold text-white uppercase tracking-wide">
                {t("footer.contact")}
              </h4>
              <ul className="mt-4 space-y-2">
                <li className="text-sm text-gray-400">{t("footer.phone")}</li>
                <li className="text-sm text-gray-400">{t("footer.email")}</li>
              </ul>
              <h4 className="mt-6 text-sm font-semibold text-white uppercase tracking-wide">
                {t("footer.office")}
              </h4>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                {t("footer.address")}
              </p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <a href={siteInfo.weibo} className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4" rel="nofollow">
                {t("footer.social_weibo")}
              </a>
              <span className="text-sm text-gray-400">{t("footer.social_wechat")}：{siteInfo.wechat}</span>
              <a href={siteInfo.bilibili} className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4" rel="nofollow">
                {t("footer.social_bilibili")}
              </a>
            </div>
            <p className="text-sm text-gray-500">{t("footer.copyright")}</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4">
                {t("footer.privacy")}
              </a>
              <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4">
                {t("footer.terms")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
